<template>
  <router-link
    :to="{ name: 'settings' }"
    class="reading-streak-widget16464"
    :class="{ 'reading-streak-widget16464--slotted': $slots.default }"
  >
    <div class="reading-streak-widget16464__title-wrapper">
      <h2 class="reading-streak-widget16464__title">{{ title }}</h2>
      <ReadingStreakIcon16464
        class="reading-streak-widget16464__icon"
        :active="streakData.remaining_seconds <= 0"
      />
    </div>

    <div v-if="$slots.default" class="reading-streak-widget16464__slot">
      <slot></slot>
    </div>

    <div class="reading-streak-widget16464__subtitle-wrapper">
      <p class="reading-streak-widget16464__subtitle">{{ subtitle }}</p>
      <p class="reading-streak-widget16464__remaining-title">
        {{ remainingTimeLabel }}
      </p>
    </div>

    <div
      class="reading-streak-widget16464__progress-bar"
      :class="{
        'reading-streak-widget16464__progress-bar--full':
          elapsedTime >= STREAK_GOAL_MINUTES,
        'reading-streak-widget16464__progress-bar--empty': elapsedTime <= 0,
      }"
      :style="{
        '--streak-progress-position': elapsedTime,
        '--streak-goal-minutes': STREAK_GOAL_MINUTES,
      }"
    ></div>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ReadingStreakData16464 } from '@/models/interfaces'

import ReadingStreakIcon16464 from './ReadingStreakIcon16464.vue'

const STREAK_GOAL_MINUTES = 10

const props = defineProps<{
  streakData: ReadingStreakData16464
}>()

const remainingMinutes = computed(() => {
  return Math.ceil(props.streakData.remaining_seconds / 60)
})

const elapsedTime = computed(() => {
  return Math.max(0, STREAK_GOAL_MINUTES - remainingMinutes.value)
})

const title = computed(() => {
  return props.streakData.streak_days > 0
    ? `${props.streakData.streak_days}-day streak`
    : 'Start your streak'
})

const subtitle = computed(() => {
  if (props.streakData.remaining_seconds <= 0) {
    return "You're awesome! Keep the streak tomorrow"
  }

  if (props.streakData.streak_days > 0) {
    return `Read ${STREAK_GOAL_MINUTES} minutes to keep your streak`
  }

  return remainingMinutes.value < STREAK_GOAL_MINUTES
    ? `Read ${STREAK_GOAL_MINUTES} minutes to complete your first streak`
    : `Read ${STREAK_GOAL_MINUTES} minutes to start your streak`
})

const remainingTimeLabel = computed(() => {
  return props.streakData.remaining_seconds > 0
    ? `${elapsedTime.value}/${STREAK_GOAL_MINUTES} min`
    : 'Done'
})
</script>

<style lang="scss" scoped>
@property --streak-border-c1 {
  syntax: '<color>';
  inherits: false;
  initial-value: rgba(255, 231, 155, 1);
}

@property --streak-border-c2 {
  syntax: '<color>';
  inherits: false;
  initial-value: rgba(255, 206, 155, 1);
}

.reading-streak-widget16464 {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  min-height: 94px;
  padding: 16px 28px;
  border: 1px solid transparent;
  border-radius: 12px;
  --streak-border-c1: rgba(255, 231, 155, 1);
  --streak-border-c2: rgba(255, 206, 155, 1);
  background:
    linear-gradient(
        135deg,
        rgba(255, 200, 142, 1) 0%,
        rgba(255, 223, 121, 1) 100%
      )
      padding-box,
    linear-gradient(
        135deg,
        var(--streak-border-c1) 0%,
        var(--streak-border-c2) 100%
      )
      border-box;
  transition: all 0.2s ease-in-out;
  transition-property: --streak-border-c1, --streak-border-c2;

  @include hover {
    cursor: pointer;
    --streak-border-c1: rgba(255, 113, 36, 1);
    --streak-border-c2: rgba(208, 74, 0, 1);
  }

  @media (max-width: $w-tablet-s) {
    padding: 16px;
  }

  &__title-wrapper,
  &__subtitle-wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  &__title-wrapper {
    margin-bottom: 4px;
  }

  &__subtitle-wrapper {
    margin-bottom: 8px;
  }

  &__title {
    font-size: 18px;
    line-height: 20px;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: $c-black-15;
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
  }

  &__slot {
    margin-top: 4px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba($c-black-15, 0.15);
  }

  &__slot + &__subtitle-wrapper {
    margin-bottom: 4px;
  }

  &__subtitle,
  &__remaining-title {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.02em;
    margin: 0;
    color: $c-black-15;
  }

  &__remaining-title {
    font-weight: 500;
  }

  &__progress-bar {
    position: relative;
    width: 100%;
    height: 12px;
    border-radius: 12px;
    background: rgba($c-black-15, 0.15);

    // The filled part of the progress bar.
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: calc(
        (var(--streak-progress-position) / var(--streak-goal-minutes)) * 100%
      );
      height: 100%;
      border-radius: 12px;
      background: linear-gradient(
        90deg,
        rgba(255, 113, 36, 1) 0%,
        rgba(208, 74, 0, 1) 100%
      );
      filter: drop-shadow(0px 0px 6px rgba(#ff8d50, 0.8));
    }

    // The handle at the end of the progress bar.
    &:after {
      content: '';
      position: absolute;
      top: 2px;
      left: calc(
        (var(--streak-progress-position) / var(--streak-goal-minutes)) * 100%
      );
      width: 8px;
      height: 8px;
      border-radius: 8px;
      margin-left: -12px;
      background: $c-white;
      filter: drop-shadow(0px 0px 6px $c-white);
    }
  }

  &__progress-bar--full {
    &:after {
      display: none;
    }
  }

  &__progress-bar--empty {
    &:before {
      width: 12px;
    }

    &:after {
      left: 0;
      margin-left: 2px;
    }
  }
}
</style>
